<script setup>
// Measuring diagram drawn as SVG rather than shipped as an image: it weighs
// nothing, stays sharp on any screen and uses the brand palette.
//
// Both variants draw the window itself at nearly the same size — the opening in
// one, the frame in the other — so the two read as one window mounted two ways
// rather than as two unrelated sketches. What differs between them is where the
// blind sits, which is the whole point.
//
// Type is set at 18/19 in a 420-wide viewBox. On a 390px phone the SVG renders
// at ~342px, so those land at ~15/16px: the same size as the prose around them.
// This is the one drawing someone reads with a tape measure in hand.
//
// Marker ids carry a suffix because both diagrams live on the same page and a
// duplicated id breaks the arrowheads on the second one.
const props = defineProps({
  variant: {
    type: String,
    required: true,
    validator: (v) => [ 'inside', 'outside' ].includes(v)
  }
})

const id = `cl-${props.variant}`
</script>

<template>
  <svg
    viewBox="0 0 420 320"
    class="h-auto w-full"
    role="img"
    :aria-label="variant === 'inside'
      ? 'Ventana instalada entre paredes: se mide el ancho y el alto del vano y se resta medio centímetro por lado al ancho'
      : 'Ventana con la cortina montada sobre el muro: se agregan diez centímetros por lado al marco'"
  >
    <defs>
      <marker
        :id="`${id}-arrow`"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#9a5f1f" />
      </marker>
    </defs>

    <rect x="0" y="0" width="420" height="320" rx="4" fill="#e6dac9" />

    <template v-if="variant === 'inside'">
      <!-- The opening: outer edge is the face of the wall, inner edge the back
           of the reveal. The diagonals give it depth. -->
      <path d="M80 50 L100 66 L100 254 L80 270 Z" fill="#c3b096" />
      <path d="M80 50 L340 50 L320 66 L100 66 Z" fill="#dccdb8" />
      <path d="M340 50 L340 270 L320 254 L320 66 Z" fill="#b9a68c" />
      <path d="M80 270 L340 270 L320 254 L100 254 Z" fill="#b09c81" />

      <!-- Window at the back of the reveal -->
      <rect x="100" y="66" width="220" height="188" fill="#f8f3ea" />
      <rect
        x="100" y="66" width="220" height="188"
        fill="none" stroke="#241b15" stroke-width="3"
      />
      <line x1="210" y1="66" x2="210" y2="254" stroke="#241b15" stroke-width="3" />

      <!-- Width dimension. The label breaks the mullion, the way a dimension
           text breaks the line it crosses on a real drawing. -->
      <line
        x1="106" y1="112" x2="314" y2="112"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <rect x="140" y="82" width="140" height="24" fill="#f8f3ea" />
      <text x="210" y="100" text-anchor="middle" class="dimension">Ancho del vano</text>

      <!-- What gets subtracted on each side -->
      <line x1="107" y1="150" x2="107" y2="240" stroke="#9a5f1f" stroke-width="2.5" stroke-dasharray="6 5" />
      <line x1="313" y1="150" x2="313" y2="240" stroke="#9a5f1f" stroke-width="2.5" stroke-dasharray="6 5" />
      <text x="146" y="206" text-anchor="middle" class="value">−0,5 cm</text>
      <text x="274" y="206" text-anchor="middle" class="value">−0,5 cm</text>

      <!-- Height dimension, drawn on the wall face the way a plan would do it -->
      <line
        x1="52" y1="52" x2="52" y2="268"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <text
        x="34" y="160" text-anchor="middle" class="dimension"
        transform="rotate(-90 34 160)"
      >Alto exacto</text>
    </template>

    <template v-else>
      <!-- The frame is drawn 208×176, within a few percent of the 220×188
           opening in the other diagram, because it is the same window seen the
           other way. Matching the frames — not the outer boxes — is what makes
           the pair read at one scale.

           The blind then extends 50 px past it on all four sides: equal on every
           side, which is what "+10 cm por lado" means, and wide enough that a
           dimension arrow with two heads still shows line between them. -->
      <rect x="56" y="30" width="308" height="276" fill="#9a5f1f" opacity="0.08" />
      <rect
        x="56" y="30" width="308" height="276"
        fill="none" stroke="#9a5f1f" stroke-width="2.5" stroke-dasharray="7 5"
      />
      <!-- Above the shape it names, so it cannot be read as labelling the
           overhang band instead. -->
      <text x="56" y="21" text-anchor="start" class="value">Cortina</text>

      <!-- Frame sitting on the wall, with no reveal to hide in -->
      <rect x="106" y="80" width="208" height="176" fill="#f8f3ea" />
      <rect
        x="106" y="80" width="208" height="176"
        fill="none" stroke="#241b15" stroke-width="3"
      />
      <line x1="210" y1="80" x2="210" y2="256" stroke="#241b15" stroke-width="3" />
      <rect x="160" y="155" width="100" height="26" fill="#f8f3ea" />
      <text x="210" y="175" text-anchor="middle" class="dimension">Marco</text>

      <!-- The overhang, the same on all four sides -->
      <line
        x1="56" y1="120" x2="106" y2="120"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <text
        x="81" y="205" text-anchor="middle" class="value"
        transform="rotate(-90 81 205)"
      >+10 cm</text>

      <line
        x1="314" y1="120" x2="364" y2="120"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <text
        x="339" y="205" text-anchor="middle" class="value"
        transform="rotate(-90 339 205)"
      >+10 cm</text>

      <line
        x1="170" y1="30" x2="170" y2="80"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <text x="186" y="61" text-anchor="start" class="value">+10 cm</text>

      <line
        x1="170" y1="256" x2="170" y2="306"
        stroke="#9a5f1f" stroke-width="2.5"
        :marker-start="`url(#${id}-arrow)`"
        :marker-end="`url(#${id}-arrow)`"
      />
      <text x="186" y="287" text-anchor="start" class="value">+10 cm</text>
    </template>
  </svg>
</template>

<style scoped>
.dimension {
  fill: #241b15;
  font-family: "Karla", ui-sans-serif, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 600;
}

.value {
  fill: #9a5f1f;
  font-family: "Karla", ui-sans-serif, system-ui, sans-serif;
  font-size: 19px;
  font-weight: 700;
}
</style>
